import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { FiChevronDown, FiCheck } from "react-icons/fi";
import { AnimatePresence, motion } from "framer-motion";

/**
 * Custom select u dizajn jeziku sajta (native <select> ne može da se stilizuje).
 * API: value, onChange(value), options: [{ value, label }], placeholder, disabled.
 */
const CustomSelect = ({
  value,
  onChange,
  options = [],
  placeholder = "Izaberi…",
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  const selected = options.find((o) => String(o.value) === String(value));

  return (
    <Wrap ref={ref}>
      <Trigger
        type="button"
        $open={open}
        disabled={disabled}
        onClick={() => setOpen((p) => !p)}
      >
        <span className={selected ? "" : "ph"}>
          {selected ? selected.label : placeholder}
        </span>
        <Chevron $open={open}>
          <FiChevronDown size={16} />
        </Chevron>
      </Trigger>

      <AnimatePresence>
        {open && (
          <Panel
            as={motion.div}
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.18, ease: [0.22, 0.72, 0.18, 1] }}
          >
            {options.length === 0 ? (
              <EmptyOpt>Nema opcija</EmptyOpt>
            ) : (
              options.map((o) => (
                <Option
                  key={o.value}
                  $active={String(o.value) === String(value)}
                  onClick={() => {
                    onChange(o.value);
                    setOpen(false);
                  }}
                >
                  <span>{o.label}</span>
                  {String(o.value) === String(value) && <FiCheck size={14} />}
                </Option>
              ))
            )}
          </Panel>
        )}
      </AnimatePresence>
    </Wrap>
  );
};

export default CustomSelect;

const Wrap = styled.div`
  position: relative;
  width: 100%;
`;

const Trigger = styled.button`
  width: 100%;
  height: 4.4rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0 1.3rem;
  background: var(--bg0, #0a0a0c);
  border: 1px solid
    ${(props) =>
      props.$open
        ? "var(--red, #fe5f55)"
        : "var(--line2, rgba(255,255,255,0.14))"};
  border-radius: 0.4rem;
  color: var(--text, #f4f2ee);
  font-family: "Poppins-Regular";
  font-size: 1.3rem;
  cursor: pointer;
  text-align: left;
  transition: border-color 0.25s;

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .ph {
    color: var(--muted2, #5c5a63);
  }

  &:hover {
    border-color: ${(props) =>
      props.$open ? "var(--red, #fe5f55)" : "var(--muted2, #5c5a63)"};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Chevron = styled.span`
  display: flex;
  color: var(--muted, #8e8c94);
  transition: transform 0.25s var(--ease, ease);
  transform: rotate(${(props) => (props.$open ? "180deg" : "0deg")});
  flex-shrink: 0;
`;

const Panel = styled.div`
  position: absolute;
  top: calc(100% + 0.6rem);
  left: 0;
  right: 0;
  z-index: 900;
  background: var(--bg2, #17171c);
  border: 1px solid var(--line2, rgba(255, 255, 255, 0.14));
  border-radius: 0.4rem;
  max-height: 26rem;
  overflow-y: auto;
  box-shadow: 0 1.6rem 4rem -1.2rem rgba(0, 0, 0, 0.85);
  padding: 0.4rem;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--line2, rgba(255, 255, 255, 0.14));
    border-radius: 3px;
  }
`;

const Option = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.2rem;
  border-radius: 0.3rem;
  font-family: ${(props) =>
    props.$active ? '"Poppins-SemiBold"' : '"Poppins-Regular"'};
  font-size: 1.3rem;
  color: ${(props) =>
    props.$active ? "var(--red, #fe5f55)" : "var(--text, #f4f2ee)"};
  cursor: pointer;
  transition: background 0.15s;

  svg {
    color: var(--red, #fe5f55);
    flex-shrink: 0;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.04);
  }
`;

const EmptyOpt = styled.div`
  padding: 1.2rem;
  font-family: "Poppins-Regular";
  font-size: 1.25rem;
  color: var(--muted2, #5c5a63);
  text-align: center;
`;
